import React, { useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { CheckIcon, ChevronDownIcon, ClearIcon } from "./icons";
import { IFilterOption } from "./constants";
import { useDismiss } from "./useDismiss";
import styles from "./styles.module.scss";

interface Props {
  icon: React.ReactNode;
  placeholder: string;
  options: IFilterOption[];
  value: string;
  onChange: (value: string) => void;
  /** Shows an (x) that resets the value to "" while one is picked. */
  clearable?: boolean;
  /** Highlight the control when the value differs from this (e.g. the default sort). */
  neutralValue?: string;
  /** Opens the menu toward the inline end, for controls on the far side of the bar. */
  alignEnd?: boolean;
}

const FilterSelect: React.FC<Props> = ({
  icon,
  placeholder,
  options,
  value,
  onChange,
  clearable,
  neutralValue = "",
  alignEnd,
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);

  const selected = options.find((option) => option.id === value);
  const isActive = !!value && value !== neutralValue;
  const showClear = clearable && !!value;

  return (
    <div ref={ref} className={styles.field}>
      <div
        className={classNames(styles.control, {
          [styles.controlActive]: isActive,
          [styles.controlOpen]: open,
        })}>
        <button
          type="button"
          className={styles.trigger}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}>
          <span className={styles.controlIcon}>{icon}</span>
          <span
            className={classNames(styles.controlText, {
              [styles.placeholder]: !selected,
            })}>
            {selected?.name || placeholder}
          </span>
          {!showClear && <ChevronDownIcon className={styles.chevron} />}
        </button>
        {showClear && (
          <button
            type="button"
            className={styles.clearButton}
            aria-label={t("lbl_org_act_clear") as string}
            onClick={() => onChange("")}>
            <ClearIcon />
          </button>
        )}
      </div>

      {open && (
        <ul
          role="listbox"
          className={classNames(styles.popover, styles.menu, {
            [styles.popoverEnd]: alignEnd,
          })}>
          {options.map((option) => {
            const isSelected = option.id === value;
            return (
              <li
                key={option.id}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                className={classNames(styles.menuItem, {
                  [styles.menuItemSelected]: isSelected,
                })}
                onClick={() => {
                  onChange(option.id);
                  close();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onChange(option.id);
                    close();
                  }
                }}>
                <span>{option.name}</span>
                {isSelected && <CheckIcon className={styles.menuCheck} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FilterSelect;
